import { Button, Container, Element, Label } from '@playcanvas/pcui';

import { Events } from '../events';
import { localize } from './localization';
import cameraFrameSelectionSvg from './svg/camera-frame-selection.svg';
import cameraResetSvg from './svg/camera-reset.svg';
import centersSvg from './svg/centers.svg';
import colorPanelSvg from './svg/color-panel.svg';
import ringsSvg from './svg/rings.svg';
import evidenceSvg from './svg/evidence.svg';
import evidenceListSvg from './svg/evidence-list.svg';
import { Tooltips } from './tooltips';


const createSvg = (svgString: string) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement;
};

class RightToolbar extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'right-toolbar'
        };

        super(args);

        this.dom.addEventListener('pointerdown', (event) => {
            event.stopPropagation();
        });

        const cameraReset = new Button({
            id: 'right-toolbar-camera-origin',
            class: 'right-toolbar-button'
        });

        const colorPanel = new Button({
            id: 'right-toolbar-color-panel',
            class: 'right-toolbar-toggle'
        });

        // Toggles the 3D clickable zone overlays on/off in the scene
        const clickableZonesToggle = new Button({
            id: 'right-toolbar-clickable-zones',
            class: ['right-toolbar-toggle', 'active']
        });

        // Opens / closes the evidence list panel
        const evidenceListToggle = new Button({
            id: 'right-toolbar-evidence-list',
            class: 'right-toolbar-toggle'
        });

        const options = new Button({
            id: 'right-toolbar-options',
            class: 'right-toolbar-toggle',
            icon: 'E283'
        });

        const centersDom = createSvg(centersSvg);
        const ringsDom = createSvg(ringsSvg);
        ringsDom.style.display = 'none';

        cameraReset.dom.appendChild(createSvg(cameraResetSvg));
        colorPanel.dom.appendChild(createSvg(colorPanelSvg));
        clickableZonesToggle.dom.appendChild(createSvg(evidenceSvg));
        evidenceListToggle.dom.appendChild(createSvg(evidenceListSvg));

        this.append(cameraReset);
        this.append(colorPanel);
        this.append(new Element({ class: 'right-toolbar-separator' }));
        this.append(clickableZonesToggle);
        this.append(evidenceListToggle);
        this.append(new Element({ class: 'right-toolbar-separator' }));
        this.append(options);

        tooltips.register(cameraReset, localize('tooltip.camera-reset'), 'bottom');
        tooltips.register(colorPanel, localize('tooltip.color-panel'), 'bottom');
        tooltips.register(clickableZonesToggle, localize('tooltip.clickable-zones'), 'bottom');
        tooltips.register(evidenceListToggle, localize('tooltip.evidence-list'), 'bottom');
        tooltips.register(options, localize('tooltip.view-options'), 'bottom');

        cameraReset.on('click', () => events.fire('camera.reset'));
        colorPanel.on('click', () => events.fire('colorPanel.toggleVisible'));
        clickableZonesToggle.on('click', () => events.invoke('clickableZones.toggleVisible'));
        evidenceListToggle.on('click', () => events.fire('evidencePanel.toggleVisible'));
        options.on('click', () => events.fire('viewPanel.toggleVisible'));

        events.on('colorPanel.visible', (visible: boolean) => {
            colorPanel.class[visible ? 'add' : 'remove']('active');
        });

        // Zones start visible → button starts active; sync on toggle
        events.on('clickableZones.visibilityChanged', (visible: boolean) => {
            clickableZonesToggle.class[visible ? 'add' : 'remove']('active');
        });

        events.on('evidencePanel.visible', (visible: boolean) => {
            evidenceListToggle.class[visible ? 'add' : 'remove']('active');
        });

        events.on('viewPanel.visible', (visible: boolean) => {
            options.class[visible ? 'add' : 'remove']('active');
        });
    }
}

export { RightToolbar };
